import React, { useEffect, useState } from "react";
import { getContrastColor } from '@/lib/colorUtils';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { AddTaskDialog, ListingTask } from "./AddTaskDialog";
import { getActivityLabel } from "@/lib/taskActivities";
import { logger } from "@/lib/logger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TasksManagerProps {
  northId: string;
  isPublished?: boolean;
  onUpdate: () => void;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  hoverColor?: string | null;
}

const TasksManager = ({
  northId,
  isPublished = false,
  onUpdate,
  primaryColor,
  secondaryColor,
  hoverColor,
}: TasksManagerProps) => {
  const [tasks, setTasks] = useState<ListingTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ListingTask | null>(null);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [showOnReport, setShowOnReport] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    fetchTasks();
    loadVisibilitySetting();
  }, [northId]);

  const loadVisibilitySetting = async () => {
    try {
      const { data, error } = await supabase
        .from("norths")
        .select("show_tasks_on_report")
        .eq("id", northId)
        .single();

      if (error) throw error;
      if (data?.show_tasks_on_report !== null && data?.show_tasks_on_report !== undefined) {
        setShowOnReport(data.show_tasks_on_report);
      }
    } catch (error) {
      logger.error("Error loading visibility setting:", error);
    }
  };

  const handleToggleVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from("norths")
        .update({ show_tasks_on_report: checked })
        .eq("id", northId);

      if (error) throw error;
      setShowOnReport(checked);

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (onUpdate) {
        await onUpdate();
      }

      toast({
        title: checked ? "Shown on report" : "Hidden from report",
        description: checked
          ? "Tasks will appear on the published report"
          : "Tasks are hidden from the published report",
      });
    } catch (error) {
      logger.error("Error updating visibility:", error);
      toast({
        title: "Error",
        description: "Failed to update visibility setting",
        variant: "destructive",
      });
    }
  };

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await (supabase
        .from("listing_tasks" as any)
        .select("*")
        .eq("north_id", northId)
        .order("activity_date", { ascending: false }) as any);

      if (error) throw error;
      setTasks((data || []) as unknown as ListingTask[]);

      queryClient.invalidateQueries({ queryKey: ["tasks-preview", northId] });
    } catch (error) {
      logger.error("Error fetching tasks:", error);
      toast({
        title: "Error",
        description: "Failed to load tasks",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await (supabase
        .from("listing_tasks" as any)
        .delete()
        .eq("id", taskId) as any);

      if (error) throw error;

      toast({
        title: "Task Deleted",
        description: "Task removed successfully",
      });

      fetchTasks();
      queryClient.invalidateQueries({ queryKey: ["tasks-preview", northId] });
    } catch (error) {
      logger.error("Error deleting task:", error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="p-4 md:p-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="space-y-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">
            Tasks
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Log listing activities and milestones to show sellers what you've done for their property
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-tasks-on-report"
              checked={showOnReport}
              onCheckedChange={handleToggleVisibility}
              disabled={isPublished}
              style={showOnReport ? { backgroundColor: "#22c55e" } : undefined}
            />
            <Label
              htmlFor="show-tasks-on-report"
              className="text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Show on Report
            </Label>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={() => {
                setEditingTask(null);
                setIsDialogOpen(true);
              }}
              disabled={isPublished}
              className="flex-1 sm:flex-none"
              style={{
                backgroundColor: secondaryColor || "#1b489b",
                color: getContrastColor(secondaryColor || "#1b489b"),
              }}
            >
              <Plus className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Add Task</span>
              <span className="md:hidden">Add</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tasks Table */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Loading tasks...
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 bg-muted/20 rounded-lg">
          <p className="text-muted-foreground mb-2">No tasks yet</p>
          <p className="text-sm text-muted-foreground">
            Add your first task to start tracking listing activities
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-sm font-semibold text-foreground">
                    Activity
                  </th>
                  <th className="text-left p-3 text-sm font-semibold text-foreground">
                    Title
                  </th>
                  <th className="text-left p-3 text-sm font-semibold text-foreground">
                    Date
                  </th>
                  <th className="text-right p-3 text-sm font-semibold text-foreground">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id} className="border-t hover:bg-muted/20">
                    <td className="p-3">
                      <Badge variant="secondary">
                        {getActivityLabel(t.activity_type)}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div>
                        <p className="font-medium text-foreground">{t.title}</p>
                        {t.notes && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {t.notes}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {formatDate(t.activity_date)}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingTask(t);
                            setIsDialogOpen(true);
                          }}
                          disabled={isPublished}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setDeleteTaskId(t.id)}
                          disabled={isPublished}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {tasks.map((t) => (
              <div key={t.id} className="border rounded-lg p-4 bg-card">
                <div className="flex items-start justify-between mb-3">
                  <Badge variant="secondary" className="text-xs">
                    {getActivityLabel(t.activity_type)}
                  </Badge>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditingTask(t);
                        setIsDialogOpen(true);
                      }}
                      disabled={isPublished}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => setDeleteTaskId(t.id)}
                      disabled={isPublished}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <h4 className="font-medium text-foreground mb-1">{t.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  {formatDate(t.activity_date)}
                </p>
                {t.notes && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {t.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Task Dialog */}
      <AddTaskDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        northId={northId}
        task={editingTask}
        hoverColor={hoverColor}
        onSuccess={() => {
          fetchTasks();
          setIsDialogOpen(false);
          setEditingTask(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteTaskId}
        onOpenChange={() => setDeleteTaskId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTaskId) {
                  handleDeleteTask(deleteTaskId);
                  setDeleteTaskId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TasksManager;
